import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Button, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { ID_TIPO_VISITA_PRESENCIAL, ID_TIPO_VISITA_TELEENFERMERIA } from "../../../MisVisitasCreationContent";
import MultipleAdjuntos from "../../MultipleAdjuntos";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import toast from "react-hot-toast";
import SAVE_VISITA_OTROS_PROFESIONALES_FINALIZAR, { adaptAddVisitaOtrosProfesionalesFinalizarInput } from "../../../../../../data/graphQL/mutations/Visitas/saveFinalizarVisitaOtrosProfesionales";
import { useNavigate } from "react-router-dom";
import { idTiposProfesionales } from "../../../../constants/tiposProfesionales";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import moment from "moment";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { useValidacionDistanciaVisita } from "../../../../hooks/useValidacionDistanciaVisita";
import { useValidacionDiscrepanciaHorariaVisita } from "../../../../hooks/useValidacionDiscrepanciaHorariaVisita";

const ID_COMBO_TIPO_VISITA = 1;
const ID_COMBO_PACIENTE_DOMICILIO = 3;

const COMBO_VISITA_INERLOCUTOR = 'Interlocutor';

export default function DatosVisitaRehabilitacion({ readOnly, onSaveRef, onExposeFormatted, combosVisita, idTipoProfesional, isClone }) {
  const navigate = useNavigate();
  const { validarDistancia, modal: modalDistancia } = useValidacionDistanciaVisita();
  const { validarDiscrepancia, modal: modalDiscrepancia } = useValidacionDiscrepanciaHorariaVisita();

  const [formData, setFormData] = useState({});
  const [pacienteDomicilioOptions, setPacienteDomicilioOptions] = useState([]);
  const [comboInterlocutorOptions, setComboInterlocutorOptions] = useState([]);
  const [tiposVisitas, setTiposVisitas] = useState([]);
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const idUsuario = parseInt(sessionStorage.getItem("idUsuario"), 10);

  const { visita, setVisita } = useVisita();

  const { data: dataPacienteDomicilio } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_PACIENTE_DOMICILIO }
  });

  const isOtroProfesional = useMemo(() => {

    return idTipoProfesional == idTiposProfesionales.KINESIOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSICOLOGO ||
      idTipoProfesional == idTiposProfesionales.PSIQUIATRA ||
      idTipoProfesional == idTiposProfesionales.TERAPISTA_OCUPACIONAL ||
      idTipoProfesional == idTiposProfesionales.ASISTENTE_SOCIAL ||
      idTipoProfesional == idTiposProfesionales.REHABILITADOR;
  }, [idTipoProfesional]);

  useEffect(() => {
    if (!dataPacienteDomicilio) return;
    setPacienteDomicilioOptions(adaptCombosVisitas(dataPacienteDomicilio));
  }, [dataPacienteDomicilio]);

  const [loadComboInterlocutor, { data: dataInterlocutor }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  const { data: dataComboVisitaValor } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_TIPO_VISITA }
  });

  useEffect(() => {
    if (!dataComboVisitaValor) return;
    setTiposVisitas(adaptCombosVisitas(dataComboVisitaValor));
  }, [dataComboVisitaValor]);

  useEffect(() => {
    if (!dataInterlocutor) return;
    setComboInterlocutorOptions(adaptCombosVisitas(dataInterlocutor));
  }, [dataInterlocutor]);

  useEffect(() => {
    if (visita?.idUsuarioCreador !== idUsuario && !readOnly) {
      handleChange('conformidad', false);
    }
  }, [idUsuario]);

  const [save] = useMutation(SAVE_VISITA_OTROS_PROFESIONALES_FINALIZAR);

  const [isPresencial, isVirtual] = useMemo(() => {
    const isPresencial = visita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_PRESENCIAL;
    const isVirtual = visita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_TELEENFERMERIA;

    return [isPresencial, isVirtual];
  }, [visita?.idComboVisitaValorTipoVisita]);

  const pacienteEnDomicilioMap = useMemo(() => {
    const map = {};
    pacienteDomicilioOptions.forEach((opt) => {
      if (opt.displayName.toLowerCase().includes("no")) map.NO = opt.id;
      if (opt.displayName.toLowerCase().includes("cancelado")) map.CANCELADO = opt.id;
      if (opt.displayName.toLowerCase().includes("si")) map.SI = opt.id;
    });
    return map;
  }, [pacienteDomicilioOptions]);

  const formattedInputs = useMemo(() => {

    const enDomicilio = formData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.SI;
    const noDomicilio = formData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.NO;
    const canceladaPrevioAviso = formData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.CANCELADO;

    const interlocutorPacienteId = comboInterlocutorOptions?.find((x) => x.displayName.toLowerCase().includes('paciente'))?.id;

    const isKinesiologo = idTipoProfesional === idTiposProfesionales.KINESIOLOGO;

    return inputs.map((input) => {
      const newInput = { ...input };
      const pacienteDomicilio = formData.idComboVisitaValorPacienteDomicilio;

      const hayFirma = !!visita?.firmaPaciente?.idArchivo;
      switch (input.name) {
        case "idComboVisitaValorPacienteDomicilio":
          const parsedOptions = pacienteDomicilioOptions?.map((opt) => ({ value: opt.id, label: opt.displayName }));
          // if (!hayFirma && isPresencial) {
          //   newInput.options = parsedOptions.filter((x) => x.value !== pacienteEnDomicilioMap.SI);
          // } else {
          //   newInput.options = parsedOptions;
          // }

          newInput.options = parsedOptions;
          newInput.disabled = isPresencial && hayFirma;
          newInput.hidden = !isPresencial;
          break;
        case "fechaVisita":
          const hoy = new Date();
          newInput.disableFuture = true;
          newInput.minDate = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - 2);

          if (pacienteDomicilio === pacienteEnDomicilioMap.CANCELADO) {
            newInput.maxDate = null;
            newInput.disableFuture = false;
          } else {
            newInput.maxDate = hoy;
          }

          newInput.defaultValue = hoy.toISOString().split("T")[0];
          newInput.disabled = isOtroProfesional || !!isClone;
          break;
        case "fechaFin":
          newInput.hidden = !(isVirtual || isPresencial && enDomicilio);
          break;
        case "tiempoEspera":
          newInput.hidden = !(isPresencial && noDomicilio);
          newInput.required = isPresencial && noDomicilio;
          break;
        case "evolucion":
          newInput.hidden = !isVirtual && formData?.idComboVisitaValorPacienteDomicilio !== pacienteEnDomicilioMap.SI;
          newInput.required = !newInput.hidden;
          break;
        case "explicacion":
          newInput.hidden = isVirtual || formData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.SI;
          newInput.required = !newInput.hidden;
          break;
        case "fotoPuertaAdjuntos":
          newInput.hidden = !(isPresencial && noDomicilio);
          newInput.required = isPresencial && noDomicilio;
          newInput.allowRemove = true;
          break;
        case "adjuntos":
          newInput.hidden = !(isPresencial && enDomicilio);
          newInput.allowRemove = true;
          break;
        case "conformidad":
          newInput.hidden = !(isPresencial && enDomicilio);
          newInput.disabled = (isPresencial && enDomicilio && !hayFirma) || (isPresencial && enDomicilio && !!hayFirma);
          break;
        case "obsConformidad":
          newInput.required = formData?.conformidad === false && (isPresencial && enDomicilio);
          newInput.hidden = !(formData?.conformidad === false && (isPresencial && enDomicilio));
          break;
        case "codigoAtencion":
        case "dNI":
        case "email":
          newInput.hidden = !isVirtual;
          break;
        case "nombreCompleto":
          newInput.hidden = !isVirtual || isVirtual && formData?.idComboVisitaValorInterlocutor === interlocutorPacienteId;
          break;
        case "paciente":
          newInput.hidden = !isVirtual || isVirtual && formData?.idComboVisitaValorInterlocutor !== interlocutorPacienteId;
          break;
        case 'idComboVisitaValorInterlocutor':
          newInput.hidden = !isVirtual;
          newInput.options = comboInterlocutorOptions;
          break;
        case "kinesioRespiratoria":
        case "kinesioMotora":
          newInput.hidden = !isKinesiologo || !(isPresencial || isVirtual) || (isPresencial && formData?.idComboVisitaValorPacienteDomicilio !== pacienteEnDomicilioMap.SI);
          break;
        default:
          break;
      }

      if (readOnly) newInput.disabled = true;
      return newInput;
    });
  }, [
    readOnly,
    formData?.idComboVisitaValorPacienteDomicilio,
    formData?.conformidad,
    formData?.idComboVisitaValorInterlocutor,
    formData.fechaVisita,
    comboInterlocutorOptions,
    visita?.idComboVisitaValorTipoVisita,
    visita?.firmaPaciente?.idArchivo,
    isVirtual,
    isPresencial,
    pacienteDomicilioOptions,
    pacienteEnDomicilioMap,
    visita?.idUsuarioCreador,
    isOtroProfesional,
    idUsuario
  ]
  );

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  useStepSave(onSaveRef, async (validate) => {
    if (formData.idComboVisitaValorTipoVisita == tiposVisitas.find((x) => x.displayName.toLowerCase().includes("presencial"))?.id &&
      (formData.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.CANCELADO || formData.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.NO)) {
      toast.error("No se puede avanzar con el formulario si el paciente no está en el domicilio.\n Debe completar los datos y presionar finalizar.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }
    if (validate) validateStepFields({ formattedInputs, formData });
    // const { data } = await save({ variables: { input: adaptAddDatosVisitaRehabilitacionInput(formData) } });
    // const id = data?.saveDatosVisitaRehabilitacion?.id;
    // if (!id) throw new Error("Error al guardar");
    setVisita((prev) => ({ ...prev, ...formData }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(inputs);
      setFormData(prev => ({
        ...filterVisitaData(visita, inputNames),
        idComboVisitaValorPacienteDomicilio: visita?.idComboVisitaValorPacienteDomicilio || prev?.idComboVisitaValorPacienteDomicilio,
        conformidad: visita?.conformidad || prev?.conformidad,
        idVisita: visita.idVisita
      }));
    }
  }, [visita]);

  useEffect(() => {
    if (!isPresencial) return;

    const hayFirma = !!visita?.firmaPaciente?.idArchivo;
    handleChange('idComboVisitaValorPacienteDomicilio', pacienteEnDomicilioMap?.[hayFirma ? 'SI' : 'NO']);
  }, [visita?.firmaPaciente, pacienteEnDomicilioMap, isPresencial]);

  useEffect(() => {
    const hayFirma = !!visita?.firmaPaciente?.idArchivo;
    if (isPresencial && hayFirma && formData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.SI) {
      handleChange('conformidad', true);
    } else {
      handleChange('conformidad', false);
    }
  }, [visita?.firmaPaciente, pacienteEnDomicilioMap, isPresencial, formData?.idComboVisitaValorPacienteDomicilio]);


  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const interlocutorId = combosVisita.find((c) => c.comboName === COMBO_VISITA_INERLOCUTOR)?.id;

    if (Number.isInteger(interlocutorId)) {
      loadComboInterlocutor({ variables: { idComboVisita: interlocutorId } });
    }
  }, [combosVisita, loadComboInterlocutor]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "datosVisitaRehabilitacion",
      stepName: "Datos de Visita Rehabilitación",
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  useStepSave(onSaveRef, async () => {
    const updatedVisita = { ...visita, ...formData };
    await saveFinalizacionVisita(updatedVisita);
    setVisita(updatedVisita);
  });

  const saveFinalizacionVisita = async (visitaData, finalizar) => {

    if (moment(visitaData.fechaVisita).isBefore(moment().subtract(2, 'days'))) {
      toast.error("La fecha de visita no puede ser anterior a 2 días desde la fecha actual");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    const enDomicilio = visitaData?.idComboVisitaValorPacienteDomicilio === pacienteEnDomicilioMap.SI;
    const validarFechaFin = isVirtual || (isPresencial && enDomicilio);

    if (validarFechaFin) {
      const fechaInicio = moment(visita.fechaVisita);
      const fechaFin = moment(visitaData.fechaFin);

      if (!fechaFin.isValid()) {
        toast.error("La fecha de finalización es inválida.");
        const error = new Error("Validación fallida");
        error.isCustomError = true;
        throw error;
      }

      if (fechaFin.isSameOrBefore(fechaInicio, 'minute')) {
        toast.error("La fecha de finalización debe ser posterior a la fecha de inicio de la visita.");
        const error = new Error("Validación fallida");
        error.isCustomError = true;
        throw error;
      }

      if (fechaFin.isAfter(moment())) {
        toast.error("La fecha de finalización no puede ser mayor a la fecha y hora actual");
        const error = new Error("Validación fallida");
        error.isCustomError = true;
        throw error;
      }
    }

    const coordenadas = await obtenerCoordenadas();

    const { data } = await save({
      variables: { input: adaptAddVisitaOtrosProfesionalesFinalizarInput(visitaData, finalizar, coordenadas?.latitud, coordenadas?.longitud) }
    });

    const idVisitaResult = data?.saveFinalizarVisitaOtrosProfesionales?.idVisita;
    if (!idVisitaResult) throw new Error("Error al guardar la visita");

    return idVisitaResult;
  };

  const handleGuardar = async () => {
    try {
      setFinalizarLoading(true);
      const email = formData?.email || "";
      if (isVirtual && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error("El email ingresado no es válido.");
        return;
      }
      validateStepFields({ formattedInputs, formData });

      const updatedVisita = { ...visita, ...formData };

      const resultadoValidacion = await validarDistancia(updatedVisita.idVisita, { omitirValidacion: isVirtual });
      if (!resultadoValidacion.permitir) return;

      const fechaFinConfirmada = updatedVisita.fechaFin || moment().format("YYYY-MM-DD HH:mm:ss");
      updatedVisita.fechaFin = fechaFinConfirmada;
      const resValidacionDiscrepancia = await validarDiscrepancia(updatedVisita.idVisita, updatedVisita.fechaVisita, updatedVisita.fechaFin);
      if (!resValidacionDiscrepancia.permitir) return;

      await saveFinalizacionVisita(updatedVisita, true);
      setVisita(updatedVisita);

      toast.success("Visita guardada correctamente.");
      navigate("/misVisitas");
    } catch (error) {
      console.error(error);
    } finally {
      setFinalizarLoading(false);
    }
  };

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: input.xs || 12 }} key={input.name}>
              {(input.name === 'fotoPuertaAdjuntos' || input.name === 'adjuntos') &&
                <MultipleAdjuntos
                  input={input}
                  archivos={formData[input.name]}
                  handleChange={handleChange}
                  idVisita={visita.idVisita}
                  idTipoArchivo={input.name === 'fotoPuertaAdjuntos' ? tipoArchivoVisita.FOTO_VIVIENDA : tipoArchivoVisita.DOCUMENTOS} />

              }
              {input.name !== 'fotoPuertaAdjuntos' && input.name !== 'adjuntos' &&
                <RenderInputByTypeMobile
                  input={input}
                  value={formData[input.name]}
                  key={input.name + (input.name === 'idComboVisitaValorPacienteDomicilio' ? formData[input.name] : '')}
                  handleChange={handleChange}
                  row={input.type === "multiple_boolean"} />

              }
            </Grid>
          )}
        {!readOnly && <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGuardar}
            fullWidth
            loading={finalizarLoading}>

            Finalizar
          </Button>
        </Grid>}
      </Grid>
      {modalDistancia}
      {modalDiscrepancia}
    </CardBox>);

}

const inputs = [
  {
    displayName: "Paciente en domicilio",
    name: "idComboVisitaValorPacienteDomicilio",
    type: "multiple_boolean",
    required: true,
    row: true,
    lightTitle: true
  },
  {
    displayName: "Fecha y hora de inicio",
    name: "fechaVisita",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    displayName: "Fecha y hora de fin",
    name: "fechaFin",
    type: "datetime",
    required: true,
    disableFuture: true
  },
  {
    name: "tiempoEspera",
    displayName: "Tiempo de espera (minutos)",
    required: true,
    type: "int",
    min: 0
  },
  {
    name: "kinesioRespiratoria",
    displayName: "Kinesiología respiratoria (AKR)",
    required: true,
    type: "boolean",
    lightTitle: true
  },
  {
    name: "kinesioMotora",
    displayName: "Kinesiología motora (AKM)",
    required: true,
    type: "boolean",
    lightTitle: true
  },
  {
    name: "evolucion",
    displayName: "Evolución",
    required: true,
    type: "long_string",
    maxLength: 4000
  },
  {
    name: "explicacion",
    displayName: "Explicación",
    required: true,
    type: "long_string",
  },
  {
    name: "fotoPuertaAdjuntos",
    displayName: "Foto Puerta",
    type: "file"
  },
  {
    name: "adjuntos",
    displayName: "Adjuntos",
    type: "file"
  },
  {
    name: "conformidad",
    displayName: "Conformidad",
    required: true,
    type: "boolean",
    lightTitle: true
  },
  {
    name: "obsConformidad",
    displayName: "Obs. Conformidad",
    required: true,
    type: "long_string",
    lightTitle: true
  },
  {
    name: "codigoAtencion",
    displayName: "Código de Atención",
    required: true,
    type: "string",
    maxLength: 500
  },
  {
    name: "idComboVisitaValorInterlocutor",
    displayName: "Interlocutor",
    required: true,
    type: "select"
  },
  {
    name: "nombreCompleto",
    displayName: "Nombre",
    required: true,
    type: "string",
    maxLength: 250
  },
  {
    name: 'paciente',
    displayName: 'Paciente',
    type: 'infoRow'
  },
  {
    name: "dNI",
    displayName: "DNI",
    required: true,
    type: "int",
    min: 0
  },
  {
    name: "email",
    displayName: "Email paciente / Interlocutor",
    type: "string",
    maxLength: 250,
    required: false
  }];